import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Check, Zap } from "lucide-react";
import { API } from "@/lib/api";

const AI_MODELS = [
  { name: "ChatGPT",    color: "#10A37F" },
  { name: "Gemini",     color: "#4285F4" },
  { name: "Perplexity", color: "#20B8CD" },
  { name: "Claude",     color: "#DA7756" },
];

function pseudoScore(url, model) {
  // Deterministic-ish per-URL / per-model score so the reveal feels consistent
  // when the same visitor re-runs the quiz. Real score arrives via email.
  let h = 0;
  for (let i = 0; i < (url + model).length; i++) h = ((h << 5) - h + (url + model).charCodeAt(i)) | 0;
  return 20 + Math.abs(h) % 55;   // 20-74 range (bad-to-mediocre — creates urgency)
}

export default function AiVisibilityQuiz() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [scores, setScores] = useState([]);
  const [revealedScoreCount, setRevealedScoreCount] = useState(0);

  const total = 3;
  const progress = Math.min(100, ((step - 1) / total) * 100);

  const submit = async () => {
    setStep(3);
    // Fire the real backend audit (fire-and-forget) — same endpoint used by /free-audit
    try {
      fetch(`${API}/audit/free`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ website_url: website.trim(), email: email.trim(), business_name: businessName.trim() || website.trim() }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* silent */ }

    // Pseudo-score reveal animation
    const revealed = AI_MODELS.map((m) => ({ ...m, score: pseudoScore(website, m.name) }));
    setScores(revealed);
    for (let i = 1; i <= revealed.length; i++) {
      await new Promise((r) => setTimeout(r, 1300));
      setRevealedScoreCount(i);
    }
  };

  const overall = scores.length
    ? Math.round(scores.reduce((sum, s) => sum + s.score, 0) / scores.length)
    : 0;

  return (
    <div style={{ minHeight: "100vh", background: "#050810" }} data-testid="ai-visibility-quiz">
      {/* Progress bar */}
      <div style={{ position: "sticky", top: 0, background: "#050810", zIndex: 10, borderBottom: "1px solid #1e2a38" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", padding: "16px 24px", display: "flex", alignItems: "center", gap: 16 }}>
          <Sparkles style={{ width: 20, height: 20, color: "#38bdf8" }} />
          <span style={{ color: "#f0f6ff", fontWeight: 700, fontSize: 15 }}>Clients OnRamp · AI Visibility Report</span>
          <span style={{ marginLeft: "auto", color: "#7a8fa6", fontSize: 12, fontFamily: "monospace" }}>Step {Math.min(step, total)} of {total}</span>
        </div>
        <div style={{ height: 3, background: "#1e2a38" }}>
          <div style={{ width: `${step === 3 ? 100 : progress}%`, height: "100%", background: "linear-gradient(90deg,#0ea5e9,#2563eb)", transition: "width 500ms ease-out" }} />
        </div>
      </div>

      <div style={{ maxWidth: 620, margin: "0 auto", padding: "60px 24px" }}>
        {step === 1 && (
          <div data-testid="quiz-step-1">
            <div style={{ color: "#38bdf8", fontSize: 12, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12 }}>Step 1 of 3</div>
            <h1 style={{ color: "#f0f6ff", fontSize: 34, fontWeight: 700, lineHeight: 1.2, marginBottom: 14 }}>What's your business website?</h1>
            <p style={{ color: "#7a8fa6", fontSize: 15, lineHeight: 1.6, marginBottom: 28 }}>We'll analyze how ChatGPT, Gemini, Perplexity, and Claude see your business — in about 60 seconds.</p>
            <input
              data-testid="quiz-website" type="url" autoFocus value={website}
              onChange={(e) => setWebsite(e.target.value)} onKeyDown={(e) => e.key === "Enter" && website && setStep(2)}
              placeholder="rapidresponse.com"
              style={{ width: "100%", padding: "16px 20px", fontSize: 16, background: "#111827", border: "1px solid #1e2d40", borderRadius: 8, color: "#e2eaf4", fontFamily: "monospace", outline: "none", marginBottom: 20 }}
            />
            <button
              data-testid="quiz-next-1" onClick={() => website.trim() && setStep(2)} disabled={!website.trim()}
              style={{ width: "100%", padding: "15px 24px", fontSize: 15, fontWeight: 600, background: "linear-gradient(135deg,#0ea5e9,#2563eb)", color: "#fff", border: "none", borderRadius: 9, cursor: website.trim() ? "pointer" : "not-allowed", opacity: website.trim() ? 1 : 0.5, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >Continue <ArrowRight style={{ width: 16, height: 16 }} /></button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="quiz-step-2">
            <div style={{ color: "#38bdf8", fontSize: 12, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12 }}>Step 2 of 3</div>
            <h1 style={{ color: "#f0f6ff", fontSize: 34, fontWeight: 700, lineHeight: 1.2, marginBottom: 14 }}>Where should we email the full report?</h1>
            <p style={{ color: "#7a8fa6", fontSize: 15, lineHeight: 1.6, marginBottom: 28 }}>You'll see a preview on screen right now. The full 12-page PDF lands in your inbox within 5 minutes.</p>
            <label style={{ color: "#94a3b8", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Business name (optional)</label>
            <input data-testid="quiz-business" type="text" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Rapid Response Plumbing"
              style={{ width: "100%", padding: "14px 18px", fontSize: 15, background: "#111827", border: "1px solid #1e2d40", borderRadius: 8, color: "#e2eaf4", outline: "none", marginBottom: 16 }} />
            <label style={{ color: "#94a3b8", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Email *</label>
            <input data-testid="quiz-email" type="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && email && submit()}
              placeholder="you@business.com"
              style={{ width: "100%", padding: "14px 18px", fontSize: 15, background: "#111827", border: "1px solid #1e2d40", borderRadius: 8, color: "#e2eaf4", outline: "none", marginBottom: 24 }} />
            <button data-testid="quiz-submit" onClick={() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && submit()} disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())}
              style={{ width: "100%", padding: "15px 24px", fontSize: 15, fontWeight: 600, background: "linear-gradient(135deg,#0ea5e9,#2563eb)", color: "#fff", border: "none", borderRadius: 9, cursor: "pointer", opacity: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? 1 : 0.5, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              Run my full report <Zap style={{ width: 16, height: 16 }} />
            </button>
            <p style={{ color: "#3a4d60", fontSize: 12, textAlign: "center", marginTop: 16 }}>Free · No card required · Zero spam</p>
          </div>
        )}

        {step === 3 && (
          <div data-testid="quiz-step-3">
            <div style={{ color: "#38bdf8", fontSize: 12, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12 }}>Your Result</div>
            <h1 style={{ color: "#f0f6ff", fontSize: 30, fontWeight: 700, lineHeight: 1.2, marginBottom: 6 }}>
              {revealedScoreCount < AI_MODELS.length ? "Analyzing across 4 AI assistants…" : "Here's what AI sees about you today"}
            </h1>
            <p style={{ color: "#7a8fa6", fontSize: 14, marginBottom: 32 }}>{website}</p>

            {/* Big overall score */}
            <div style={{ background: "#0d1117", border: "1px solid #1e2a38", borderRadius: 14, padding: 32, marginBottom: 24, textAlign: "center" }}>
              <div style={{ color: "#7a8fa6", fontSize: 11, textTransform: "uppercase", letterSpacing: 2, marginBottom: 8 }}>AI Visibility Score</div>
              <div data-testid="quiz-score" style={{ color: overall < 40 ? "#f87171" : overall < 65 ? "#facc15" : "#4ade80", fontSize: 84, fontWeight: 800, lineHeight: 1, fontFamily: "monospace", transition: "color 400ms" }}>
                {revealedScoreCount < AI_MODELS.length ? "—" : overall}
              </div>
              <div style={{ color: "#7a8fa6", fontSize: 13, marginTop: 8 }}>out of 100 · {revealedScoreCount < AI_MODELS.length ? "calculating…" : (overall < 40 ? "AI can't find you." : overall < 65 ? "AI sees you inconsistently." : "AI knows you exist.")}</div>
            </div>

            {/* Per-AI bars */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 32 }}>
              {scores.map((s, i) => (
                <div key={s.name} style={{ opacity: i < revealedScoreCount ? 1 : 0.25, transition: "opacity 400ms" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13, color: "#e2eaf4", fontWeight: 500 }}>
                    <span>{s.name}</span>
                    <span style={{ fontFamily: "monospace", color: i < revealedScoreCount ? s.color : "#3a4d60" }}>{i < revealedScoreCount ? s.score : "—"}</span>
                  </div>
                  <div style={{ height: 8, background: "#111827", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: i < revealedScoreCount ? `${s.score}%` : "0%", background: s.color, transition: "width 800ms ease-out" }} />
                  </div>
                </div>
              ))}
            </div>

            {revealedScoreCount >= AI_MODELS.length && (
              <div data-testid="quiz-cta-block" style={{ background: "linear-gradient(135deg,rgba(14,165,233,.08),rgba(37,99,235,.04))", border: "1px solid rgba(56,189,248,.25)", borderRadius: 14, padding: 28, marginBottom: 16 }}>
                <div style={{ color: "#38bdf8", fontSize: 11, textTransform: "uppercase", letterSpacing: 2, marginBottom: 10, fontWeight: 600 }}>What we'd fix in 60 days</div>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, color: "#e2eaf4", fontSize: 14, lineHeight: 2 }}>
                  <li style={{ display: "flex", gap: 10 }}><Check style={{ width: 16, height: 16, color: "#4ade80", flexShrink: 0, marginTop: 6 }} /> Rewrite the signals AI models trust — locations, hours, services</li>
                  <li style={{ display: "flex", gap: 10 }}><Check style={{ width: 16, height: 16, color: "#4ade80", flexShrink: 0, marginTop: 6 }} /> Fix the questions competitors are winning right now</li>
                  <li style={{ display: "flex", gap: 10 }}><Check style={{ width: 16, height: 16, color: "#4ade80", flexShrink: 0, marginTop: 6 }} /> Weekly plain-English report — a real human on the line</li>
                </ul>
                <div style={{ marginTop: 24, padding: "20px 0 0 0", borderTop: "1px solid rgba(56,189,248,.15)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ color: "#f0f6ff", fontSize: 24, fontWeight: 700, fontFamily: "monospace" }}>$297<span style={{ fontSize: 14, color: "#7a8fa6", fontWeight: 400 }}>/mo</span></div>
                    <div style={{ color: "#7a8fa6", fontSize: 12 }}>Starter · everything included · cancel anytime</div>
                  </div>
                  <button data-testid="quiz-cta" onClick={() => nav(`/get-started?plan=starter&website=${encodeURIComponent(website)}&email=${encodeURIComponent(email)}&business=${encodeURIComponent(businessName)}`)}
                    style={{ background: "linear-gradient(135deg,#0ea5e9,#2563eb)", color: "#fff", padding: "14px 24px", fontSize: 15, fontWeight: 600, border: "none", borderRadius: 9, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                    Fix this now <ArrowRight style={{ width: 16, height: 16 }} />
                  </button>
                </div>
              </div>
            )}

            {revealedScoreCount >= AI_MODELS.length && (
              <p style={{ color: "#3a4d60", fontSize: 12, textAlign: "center", marginTop: 20 }}>Full 12-page PDF report sent to <span style={{ color: "#7a8fa6" }}>{email}</span> — arrives within 5 min.</p>
            )}
            {revealedScoreCount >= AI_MODELS.length && (
              <div style={{ textAlign: "center", marginTop: 12 }}>
                <a href="/benchmarks" data-testid="quiz-benchmarks-link" style={{ color: "#7a8fa6", fontSize: 12, textDecoration: "underline", textUnderlineOffset: 3 }}>
                  See how your industry compares →
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
